
import React, { useContext, useEffect, useState } from 'react'
import { SpinnerComponent } from 'react-element-spinner'
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	Col,
	Container,
	Label,
	ListGroup,
	ListGroupItem,
	Row,
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

import { AuctionsContext } from '../../../../contexts/AuctionsProvider'
import { SessionContext } from '../../../../contexts/SessionProvider'
import Enumeration from '../../../../enumeration'
import PageTitleContextMenu from '../../../../components/PageTitles/PageTitleContextMenu'
import AuctionsNavBar from '../../../../components/Navbars/AuctionsNavBar'
import AuctionsService from '../../../../services/AuctionsService'
import {subscribe, trigger, unsubscribe} from '../../../../events/customEvents'
import SiteService from '../../../../services/SiteService'
import CounterBidModal from '../../../../components/Modals/CounterBidModal'

const AuctionBids = ({ embedded = false, onRequestClose, onSelectDetailTab }) =>
{
	const { t } = useTranslation()
	const [isBusy, setIsBusy] = useState(false)
	const auctionsContext = useContext(AuctionsContext)
	const sessionContext = useContext(SessionContext)
	const [auctionTierMemberBid, setAuctionTierMemberBid] = useState(undefined)
	const [memberBidCounters, setMemberBidCounters] = useState([])
	const [showCounterBidModal, setShowCounterBidModal] = useState(false)
	const [auctionBidsViewModel, setAuctionBidsViewModel] = useState({ bids: [], offers: [], reservePrice: 0, isReserveMet: false })
	const navigate = useNavigate()
	
	useEffect(() =>
	{
		subscribe('counterMemberBidModalClosed', handleCounterMemberBidModalClosed)
		
		retrieveAuctionBidsViewModel()
		
		return () =>
		{
			unsubscribe('counterMemberBidModalClosed', handleCounterMemberBidModalClosed)
		}
	}, [])
	
	const memberItemStateLabel = (memberItem) =>
	{
		let label = ''
		if (memberItem !== null && memberItem !== undefined)
		{
			if (memberItem.accepted !== null && memberItem.accepted === true)
			{
				label = 'Accepted'
			}
			if (memberItem.rejected !== null && memberItem.rejected === true)
			{
				label = 'Rejected'
			}
		}
		return label
	}
	
	const handleGoBack = () =>
	{
		if (embedded)
		{
			if (typeof onRequestClose === 'function') onRequestClose()
			return
		}
		navigate('/secure/auctions-listing')
	}
	
	const handleConfirmBid = (entityId, entityTypeId) =>
	{
		setIsBusy(true)
		AuctionsService
			.confirmTierMemberBid(entityId, entityTypeId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				trigger('raiseNotification', { title: 'Update Success', message: 'The bid was accepted.', type: 'success', duration: 5 })
				handleGoBack()
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleCounterBid = (auctionTierMemberBid) =>
	{
		setAuctionTierMemberBid(auctionTierMemberBid)
		retrieveMemberBidCounters(auctionTierMemberBid.bidItemEntityId)
		setShowCounterBidModal(true)
	}
	
	const retrieveMemberBidCounters = (auctionTierMemberBidId) =>
	{
		setIsBusy(true)
		AuctionsService
			.getMemberBidCounters(auctionTierMemberBidId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setMemberBidCounters(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleCounterMemberBidModalClosed = (event) =>
	{
		setAuctionTierMemberBid(undefined)
		setShowCounterBidModal(false)
		retrieveAuctionBidsViewModel()
	}
	
	const retrieveAuctionBidsViewModel = () =>
	{
		setIsBusy(true)
		AuctionsService
			.getBidsAndOffersViewModel(auctionsContext.auction.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setAuctionBidsViewModel(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	return (
		<div style={{
			zIndex: 500
		}}>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			{
				!embedded &&
				<PageTitleContextMenu
					heading={ t('Auctions.Bids.Title') }
					subheading={ auctionsContext.auction.auctionVehicle.vehicle.year + ' ' +
						auctionsContext.auction.auctionVehicle.vehicle.make + ' ' +
						auctionsContext.auction.auctionVehicle.vehicle.model + ' / ' +
						auctionsContext.auction.auctionVehicle.vehicle.trim + ' / ' +
						auctionsContext.auction.auctionVehicle.vehicle.serialNumber
					}
					icon="pe-7s-ticket icon-gradient bg-premium-dark"
					headerOptions={ { showBack: true, backCommand: () => { handleGoBack() }, showReload: false, reloadCommand: null } }
				/>
			}
			<Container className={ embedded ? "" : "mt--6" } fluid>
				<Row>
					<div className="col">
						<Card>
							<CardHeader style={{padding: '0.5rem 0.75rem'}}>
								<AuctionsNavBar
									parentComponent={'auction-bids'}
									onSelect={ embedded ? onSelectDetailTab : undefined }
								/>
							</CardHeader>
							<CardBody>
								<Row>
									<Col md="6">
										<Card>
											<CardHeader>
												<Row>
													<Col sm={6}><Label>{t('Auctions.Bids.Current_Bids')}</Label></Col>
													<Col sm={6}>
														<div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
															<small>Reserve Price:</small>{' '}
															<small>{ auctionBidsViewModel.reservePrice ? auctionBidsViewModel.reservePrice.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</small>
															{' '}
															{ auctionBidsViewModel.reservePrice > 0 && (
																<span 
																	className={`badge ${auctionBidsViewModel.isReserveMet ? 'bg-success' : 'bg-warning'}`}
																	style={{ fontSize: 'x-small' }}
																>
																	{ auctionBidsViewModel.isReserveMet ? 'Reserve Met' : 'Reserve Not Met' }
																</span>
															)}
														</div>
													</Col>
												</Row>
											</CardHeader>
											<CardBody>
												<ListGroup className="list my--3" flush>
													{
														auctionBidsViewModel.bids.map((bidItem, idx) =>
														{
															return (
																<ListGroupItem className="px-0" key={ idx }>
																	<Row className="align-items-center">
																		<div className="col ml--2">
																			<Label>
																				<a href="#" onClick={(e) => e.preventDefault()}>
																					{ bidItem.fullName + ' (' + bidItem.dealerName + ' - ' + bidItem.oemName + ')'}
																				</a>
																			</Label>
																			<div>
																				<small>{`${bidItem.bidItemEntityTypeName} Time:`}</small>{' '}
																				<small>{ (bidItem.bidTime !== null) ? dayjs(bidItem.bidTime).format('MMM DD, YYYY HH:mm') : '' }</small>
																			</div>
																			<div>
																				<small>Iteration:</small>{' '}
																				<small className="text-primary" style={{ fontWeight: 'bold' }}>{ bidItem.iterationName || 'N/A' }</small>
																			</div>
																			<div>
																				<small>{`${bidItem.bidItemEntityTypeName} Amount:`}</small>{' '}
																				<span className="text-success"><small style={{ fontWeight: 'bold' }}>{ bidItem.bidAmount ? bidItem.bidAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</small></span>
																			</div>
																			<div>
																				<small>Note:</small>{' '}
																				<span className="text-success"><small style={{ fontWeight: 'bold' }}>{ bidItem.note }</small></span>
																			</div>
																			<div>
																				<small># Counter Bids:</small>{' '}
																				<span className="text-success"><small style={{ fontWeight: 'bold' }}>{ bidItem.counterBidCount }</small></span>
																			</div>
																		</div>
																		<Col className="col-auto">
																			{
																				(bidItem.bidByUserGroupId !== sessionContext.loginSession.user.usersRoleMemberships[0].userRole.userGroupId) &&
																				<>
																					<Button
																						color="primary"
																						size="sm"
																						onClick={ () => handleConfirmBid(bidItem.bidItemEntityId, bidItem.bidItemEntityTypeId) }
																						disabled={ (idx > 0) || ((auctionsContext.auction.auctionStateId !== Enumeration.AuctionStates.Released && auctionsContext.auction.auctionStateId !== Enumeration.AuctionStates.InSettlement) || (bidItem.accepted !== null && bidItem.accepted)) }
																						style={{ marginRight: '5px' }}
																					>
																						Accept
																					</Button>
																					<Button
																						color="secondary"
																						size="sm"
																						onClick={ () => handleCounterBid(bidItem) }
																						disabled={ (idx > 0) || ((auctionsContext.auction.auctionStateId !== Enumeration.AuctionStates.Released && auctionsContext.auction.auctionStateId !== Enumeration.AuctionStates.InSettlement) || (bidItem.rejected !== null && bidItem.rejected)) }
																					>
																						Counter
																					</Button>
																				</>
																			}
																			<div><small>{ memberItemStateLabel(bidItem) }</small></div>
																		</Col>
																	</Row>
																</ListGroupItem>
															)
														})
													}
												</ListGroup>
											</CardBody>
										</Card>
									</Col>
									<Col md="6">
										<Card>
											<CardHeader><Label>{ t('Auctions.Bids.Current_Offers') }</Label></CardHeader>
											<CardBody>
												<ListGroup className="list my--3" flush>
													{
														auctionBidsViewModel.offers.map((offer, idx) => (
															<ListGroupItem className="px-0" key={ idx }>
																<Row className="align-items-center">
																	<div className="col ml--2">
																		<Label>
																			<a href="#" onClick={(e) => e.preventDefault()}>
																				{offer.userName}
																			</a>
																		</Label>
																		<div>
																			<small>Offer Time:</small>{' '}
																			<small>{ (offer.buyNowTime !== null) ? dayjs(offer.buyNowTime).format('MMM DD, YYYY HH:mm') : '' }</small>
																		</div>
																		<div>
																			<small>Offer Amount:</small>{' '}
																			<span className="text-success"><small>{ offer.buyNowAmount ? offer.buyNowAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</small></span>
																		</div>
																	</div>
																	<Col className="col-auto">
																		<span className="text-success"><b><small>SOLD</small></b></span>
																		<div><small>{ memberItemStateLabel(offer) }</small></div>
																	</Col>
																</Row>
															</ListGroupItem>
														))
													}
												</ListGroup>
											</CardBody>
										</Card>
									</Col>
								</Row>
							</CardBody>
						</Card>
					</div>
				</Row>
			</Container>
			
			<CounterBidModal
				showCounterBidModal={ showCounterBidModal }
				auctionTierMemberBid={ auctionTierMemberBid }
				memberBidCounters={ memberBidCounters }
			/>
		</div>
	)
}

export default AuctionBids
